import { Link } from 'react-router-dom';
import { CompanyFacts, CtaBand, Presence, ProductCard, SectionHead } from '../components/Blocks';
import { HeroNetwork } from '../components/HeroNetwork';
import { usePageMeta } from '../components/usePageMeta';
import { company } from '../content/company';
import { products } from '../content/products';

const principles = [
  {
    n: '01',
    title: 'Security is the product',
    body: 'Every application we ship exists to protect something: a brand’s goods, a person’s phone, a professional’s reputation. Security is designed in from the first line, not added later.',
  },
  {
    n: '02',
    title: 'Privacy by default',
    body: 'We collect only what a product needs to work, tell people plainly what that is, and give them control over it.',
  },
  {
    n: '03',
    title: 'Built for real conditions',
    body: 'Our products are engineered for the networks, devices and markets our users actually live in, starting in Africa.',
  },
  {
    n: '04',
    title: 'One accountable owner',
    body: 'Every product is developed, operated and supported by Divergent Networks Limited. There is always a responsible company behind the app.',
  },
];

export function Home() {
  usePageMeta(
    company.shortName,
    'Divergent Networks Limited is a Kenyan technology company building secure digital products: VeriPlus for product verification, Tambo for phone security and Sassy for business networking.',
  );

  return (
    <>
      <section className="hero">
        <div className="hero__grid-bg" aria-hidden="true" />
        <div className="container hero__inner">
          <div className="hero__copy">
            <p className="eyebrow eyebrow--light">
              <span className="dot" aria-hidden="true" />
              <span className="eyebrow__text">
                <span className="nowrap">Inspirational Problem Solving</span>{' '}
                <span className="nowrap">· For Humanity</span>
              </span>
            </p>
            <h1 className="hero__title">
              Global
              <br />
              <span className="hero__accent">Innovative Minds.</span>
            </h1>
            <p className="hero__lede">
              {company.legalName} builds secure digital applications for product verification, phone security and
              professional connection, from our headquarters in Kenya to partners across four regions.
            </p>
            <div className="hero__actions">
              <Link className="btn btn--primary" to="/products">
                Explore our products
              </Link>
              <Link className="btn btn--ghost-light" to="/company">
                About the company
              </Link>
            </div>
          </div>
          <div className="hero__art">
            <HeroNetwork />
          </div>
        </div>
      </section>

      <section className="stats" aria-label="At a glance">
        <div className="container stats__grid">
          <div className="stat">
            <span className="stat__value">3</span>
            <span className="stat__label">Products in our portfolio</span>
          </div>
          <div className="stat">
            <span className="stat__value">4</span>
            <span className="stat__label">Operating regions</span>
          </div>
          <div className="stat">
            <span className="stat__value">24/7</span>
            <span className="stat__label">Security monitoring and support</span>
          </div>
          <div className="stat">
            <span className="stat__value">{company.asn}</span>
            <span className="stat__label">Our own registered network</span>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHead
            eyebrow="Our products"
            title="Three products. One standard of trust."
            lede="Each product solves a distinct, everyday problem, and all three are built, owned and operated by Divergent Networks Limited."
          />
          <div className="product-grid">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      <section className="section section--tint">
        <div className="container split">
          <SectionHead
            eyebrow="How we build"
            title="Principles we ship by."
            lede="Our mission is to build purposeful digital applications that address practical needs in security, verification and professional connection."
          />
          <ol className="principles">
            {principles.map((p) => (
              <li key={p.n} className="principle">
                <span className="principle__n">{p.n}</span>
                <div>
                  <h3 className="principle__title">{p.title}</h3>
                  <p className="principle__body">{p.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHead
            eyebrow="International presence"
            title="Headquartered in Kenya. Working across four regions."
            lede="A cross-border footprint gives our portfolio access to partners, supply chains and customers in Africa, Europe, the Indian Ocean and Asia."
          />
          <Presence />
        </div>
      </section>

      <section className="section section--tint">
        <div className="container split split--facts">
          <SectionHead
            eyebrow="Company information"
            title="A registered company you can verify."
            lede={
              <>
                {company.legalName} is a private limited company incorporated in the {company.jurisdiction}. These are
                our registration details. <Link to="/company#information">More about the company</Link>
              </>
            }
          />
          <CompanyFacts />
        </div>
      </section>

      <CtaBand />
    </>
  );
}
